"use client";

import { useState } from "react";
import AddParticipantsModal from "./AddParticipantsModal";
import toast from "react-hot-toast";
import { callApi } from "../../common/Apicall";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedConversation,
  setWithMessagesConversations,
  setWithMessagesGroups,
} from "../../Slices/ChatSlice";
import Swal from "sweetalert2";
import {
  IoPersonRemove,
  IoExit,
  IoPersonAdd,
  IoClose,
  IoShield,
  IoTrash,
} from "react-icons/io5";

const ParticipantsModal = ({
  selectedConversation,
  showParticipantsModal,
  setShowParticipantsModal,
  onCloseParticipantsModal,
  usersData,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showAddParticipants, setShowAddParticipants] = useState(false);
  const dispatch = useDispatch();
  const loggedInUserId = useSelector(
    (state) => state?.data?.userInfo?.data?.data?.UserId
  );
  const Api_URL = process.env.REACT_APP_URL;
  const participants = selectedConversation?.participants || [];

  // Sort participants to place the admin at the top
  const sortedParticipants = [...participants].sort((a, b) => {
    if (a.userId === selectedConversation?.createdByUserId) return -1; // Admin comes first
    if (b.userId === selectedConversation?.createdByUserId) return 1;
    return 0;
  });

  const handleAddParticipants = () => {
    setShowAddParticipants(true);
  };

  const handleAdd = async (newParticipants) => {
    setIsLoading(true);
    try {
      const res = await callApi(
        `${Api_URL}/GroupChat/Edit-Group/${selectedConversation?.value}/Add-Participants`,
        "post",
        newParticipants
      );

      const fetchConversations = async () => {
        try {
          const res = await callApi(
            `${Api_URL}/GroupChat/Users-With-GroupMessages`,
            "GET"
          );
          if (res.error) throw new Error(res.error);
          dispatch(setWithMessagesGroups(res));
          const findSelectedConversation = res?.find(
            (conversation) => conversation.value === selectedConversation?.value
          );
          dispatch(
            setSelectedConversation({
              conversation: findSelectedConversation,
              type: "group",
            })
          );
        } catch (error) {
          toast.error(`Failed to fetch conversations: ${error.message}`);
        }
      };

      if (res.error) throw new Error(res.error);
      fetchConversations();
      setIsLoading(false);
      toast.success("Participants added successfully!");
      setShowAddParticipants(false);
      onCloseParticipantsModal();
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    }
  };

  const handleRemoveParticipant = async (participantId, participantName) => {
    // Show confirmation dialog using SweetAlert2
    const result = await Swal.fire({
      title: "Remove Participant",
      text: `Are you sure you want to remove ${participantName} from the group?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#9e9e9e",
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
      },
    });

    // If the user confirms, proceed with removal
    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const res = await callApi(
          `${Api_URL}/GroupChat/Edit-Group/${selectedConversation?.value}/Remove-Participants`,
          "post",
          [participantId]
        );

        const fetchConversations = async () => {
          try {
            const res = await callApi(
              `${Api_URL}/GroupChat/Users-With-GroupMessages`,
              "GET"
            );
            if (res.error) throw new Error(res.error);
            dispatch(setWithMessagesGroups(res));
            const findSelectedConversation = res?.find(
              (conversation) =>
                conversation.value === selectedConversation?.value
            );
            dispatch(
              setSelectedConversation({
                conversation: findSelectedConversation,
                type: "group",
              })
            );
          } catch (error) {
            toast.error(`Failed to fetch conversations: ${error.message}`);
          }
        };

        if (res.error) throw new Error(res.error);
        fetchConversations();
        setIsLoading(false);
        toast.success("Participant removed successfully!");
      } catch (error) {
        setIsLoading(false);
        toast.error(error.message || "Something went wrong!");
      }
    }
  };

  const handleLeaveGroup = async () => {
    // Show confirmation dialog using SweetAlert2
    const result = await Swal.fire({
      title: "Leave Group",
      text: "Are you sure you want to leave this group? You'll need to be added back by an admin to rejoin.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#9e9e9e",
      confirmButtonText: "Leave Group",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
      },
    });

    // If the user confirms, proceed with leaving the group
    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const res = await callApi(
          `${Api_URL}/GroupChat/Edit-Group/${selectedConversation?.value}/Remove-Participants`,
          "post",
          [loggedInUserId] // Pass the logged-in user's ID to remove themselves
        );

        const fetchConversations = async () => {
          try {
            const res = await callApi(
              `${Api_URL}/GroupChat/Users-With-GroupMessages`,
              "GET"
            );
            if (res.error) throw new Error(res.error);
            dispatch(setWithMessagesGroups(res));
            // Clear the selected conversation since the user has left the group
            dispatch(setSelectedConversation(null));
          } catch (error) {
            toast.error(`Failed to fetch conversations: ${error.message}`);
          }
        };

        if (res.error) throw new Error(res.error);
        fetchConversations();
        setIsLoading(false);
        toast.success("You have left the group successfully!");
        onCloseParticipantsModal(); // Close the modal after leaving
      } catch (error) {
        setIsLoading(false);
        toast.error(error.message || "Something went wrong!");
      }
    }
  };

  // New function to handle deleting the group
  const handleDeleteGroup = async () => {
    // Show confirmation dialog with extra warning
    const result = await Swal.fire({
      title: "Delete Group",
      html: `
        <div class="flex flex-col items-center">
          <div class="text-red-500 mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p>Are you sure you want to <strong>permanently delete</strong> this group?</p>
          <p class="text-sm text-red-600 mt-2">This action cannot be undone and all messages will be lost.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#9e9e9e",
      confirmButtonText: "Delete Group",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
      },
    });

    // If the user confirms, proceed with deletion
    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        // Assuming there's an API endpoint for deleting a group
        const res = await callApi(
          `${Api_URL}/GroupChat/Delete-Group/${selectedConversation?.value}`,
          "DELETE"
        );

        const fetchConversations = async () => {
          try {
            const res = await callApi(
              `${Api_URL}/GroupChat/Users-With-GroupMessages`,
              "GET"
            );
            if (res.error) throw new Error(res.error);
            dispatch(setWithMessagesGroups(res));
            dispatch(
              setSelectedConversation({
                conversation: null,
                type: "group",
              })
            );
          } catch (error) {
            console.log(`Failed to fetch conversations: ${error.message}`);
          }
        };

        if (res.error) throw new Error(res.error);
        fetchConversations();
        setIsLoading(false);
        toast.success("Group deleted successfully!");
        onCloseParticipantsModal(); // Close the modal after deletion
      } catch (error) {
        setIsLoading(false);
        toast.error(
          error.message || "Failed to delete group. Please try again."
        );
      }
    }
  };

  // Function to generate initials from name
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${
      lastName?.charAt(0) || ""
    }`.toUpperCase();
  };

  // Function to generate a consistent color based on user ID
  const getUserColor = (userId) => {
    const colors = [
      "bg-blue-100 text-blue-800",
      "bg-green-100 text-green-800",
      "bg-purple-100 text-purple-800",
      "bg-yellow-100 text-yellow-800",
      "bg-pink-100 text-pink-800",
      "bg-indigo-100 text-indigo-800",
      "bg-red-100 text-red-800",
      "bg-teal-100 text-teal-800",
    ];

    // Convert userId to string and then create a simple hash
    const userIdStr = String(userId);
    // Use a simpler approach to generate a consistent index
    let hash = 0;
    for (let i = 0; i < userIdStr.length; i++) {
      hash = (hash + userIdStr.charCodeAt(i)) % colors.length;
    }

    return colors[hash];
  };

  const isAdmin = loggedInUserId === selectedConversation?.createdByUserId;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-[#0d4b82] px-6 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            {/* <IoPersonCircle className="text-2xl" /> */}
            Group Participants
          </h2>
          <button
            onClick={onCloseParticipantsModal}
            className="text-white hover:bg-blue-700 rounded-full p-1 transition-colors"
            aria-label="Close"
          >
            <IoClose className="w-6 h-6" />
          </button>
        </div>

        {/* Participants count */}
        <div className="px-6 py-2 bg-gray-50 border-b">
          <p className="text-gray-600">
            {participants.length}{" "}
            {participants.length === 1 ? "participant" : "participants"}
          </p>
        </div>

        {/* Scrollable container for participants list */}
        <div className="max-h-80 overflow-y-auto">
          <ul className="divide-y divide-gray-100">
            {sortedParticipants.map((participant) => {
              const isAdmin =
                participant.userId === selectedConversation?.createdByUserId;
              const isCurrentUser = participant.userId === loggedInUserId;
              const fullName = `${participant.firstName} ${participant.lastName}`;

              return (
                <li
                  key={participant?.userId}
                  className={`px-6 py-3 hover:bg-gray-50 transition-colors ${
                    isAdmin ? "bg-blue-50" : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${getUserColor(
                          participant.userId
                        )}`}
                      >
                        {getInitials(
                          participant.firstName,
                          participant.lastName
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate flex items-center">
                          {fullName}
                          {isCurrentUser && (
                            <span className="ml-1 text-xs text-gray-500">
                              (You)
                            </span>
                          )}
                          {isAdmin && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              <IoShield className="mr-1" />
                              Admin
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {participant.email}
                        </p>
                      </div>
                    </div>

                    {/* Action buttons */}
                    {loggedInUserId === selectedConversation?.createdByUserId &&
                      participant.userId !==
                        selectedConversation?.createdByUserId && (
                        <button
                          onClick={() =>
                            handleRemoveParticipant(
                              participant.userId,
                              fullName
                            )
                          }
                          disabled={isLoading}
                          className="text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors"
                          title="Remove participant"
                        >
                          <IoPersonRemove className="w-5 h-5" />
                        </button>
                      )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer with action buttons */}
        <div className="px-6 py-2 bg-gray-50 border-t">
          {/* Admin actions */}
          {isAdmin ? (
            <div className="flex flex-col space-y-3">
              <div className="flex justify-between">
                <button
                  onClick={handleAddParticipants}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  <IoPersonAdd className="mr-2" />
                  Add Participants
                </button>

                <button
                  onClick={onCloseParticipantsModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>

              {/* Delete Group button */}
              <div className="pt-2 border-t border-gray-200">
                <button
                  onClick={handleDeleteGroup}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center w-full px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-md hover:bg-red-100 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  <IoTrash className="mr-2" />
                  Delete Group
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between">
              {/* <button
                onClick={handleLeaveGroup}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
              >
                <IoExit className="mr-2" />
                Leave Group
              </button> */}

              <button
                onClick={onCloseParticipantsModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Render the AddParticipantsModal if showAddParticipants is true */}
      {showAddParticipants && (
        <AddParticipantsModal
          onClose={() => setShowAddParticipants(false)}
          onAddParticipants={handleAdd}
          usersData={usersData}
          currentParticipants={participants}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default ParticipantsModal;
